import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PbtsTagChips } from '@/components/pbts/PbtsTagChips';
import type { ProntuarioPastSessionRecord } from '../../types/prontuarioPastSession';
import { resolveSessionCardSchedule } from '../../utils/prontuarioSessionCardSchedule';

const NO_SHORT_SUMMARY_LABEL = 'não há descrição disponível';

const resolveShortSummaryDisplay = (shortSummary?: string | null): string => {
	const text = shortSummary?.trim();
	return text || NO_SHORT_SUMMARY_LABEL;
};

type ProntuarioSessionCardVariant = 'dark' | 'light';

const SESSION_CARD_THEME = {
	dark: {
		card: 'group cursor-pointer overflow-hidden rounded-xl border border-white/[0.08] bg-[#1a2430] px-4 py-3.5 transition-colors hover:bg-[#1e2a34]',
		divider: 'bg-white/[0.08]',
		day: 'font-albert text-[1.625rem] font-bold leading-none tabular-nums text-slate-100',
		month: 'truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500',
		sessionTitle:
			'truncate font-albert text-[14px] font-bold leading-tight text-slate-100',
		summaryWithText: 'text-slate-400',
		summaryEmpty: 'text-slate-500',
		chevron:
			'size-5 translate-x-0 text-slate-500 transition-[transform,color] duration-200 ease-out group-hover:translate-x-1 group-hover:text-slate-200',
		skeleton:
			'animate-pulse overflow-hidden rounded-xl border border-white/[0.08] bg-[#1a2430] px-4 py-3.5',
		skeletonBlock: 'bg-white/10',
	},
	light: {
		card: 'group cursor-pointer overflow-hidden rounded-xl border border-zinc-300 bg-white px-4 py-3.5 transition-colors hover:bg-zinc-50',
		divider: 'bg-zinc-200',
		day: 'font-albert text-[1.625rem] font-bold leading-none tabular-nums text-zinc-900',
		month: 'truncate text-[11px] font-semibold uppercase tracking-wide text-zinc-500',
		sessionTitle:
			'truncate font-albert text-[14px] font-bold leading-tight text-zinc-900',
		summaryWithText: 'text-zinc-500',
		summaryEmpty: 'text-zinc-400',
		chevron:
			'size-5 translate-x-0 text-zinc-400 transition-[transform,color] duration-200 ease-out group-hover:translate-x-1 group-hover:text-zinc-700',
		skeleton:
			'animate-pulse overflow-hidden rounded-xl border border-zinc-300 bg-white px-4 py-3.5',
		skeletonBlock: 'bg-zinc-200',
	},
} as const satisfies Record<
	ProntuarioSessionCardVariant,
	Record<string, string>
>;

const SessionCardColumnDivider = ({
	variant,
}: {
	variant: ProntuarioSessionCardVariant;
}) => (
	<div
		className={cn(
			'w-px shrink-0 self-stretch',
			SESSION_CARD_THEME[variant].divider,
		)}
		aria-hidden
	/>
);

type ProntuarioSessionCardProps = {
	sessionNumber?: string | number | null;
	dateLabel?: string | null;
	shortSummary?: string | null;
	shortSummaryTag?: string | null;
	platformSession?: Sessions.Session | null;
	onClick?: () => void;
	variant?: ProntuarioSessionCardVariant;
	isCurrent?: boolean;
};

const ProntuarioSessionCardSkeleton = ({
	variant = 'dark',
}: {
	variant?: ProntuarioSessionCardVariant;
}) => {
	const theme = SESSION_CARD_THEME[variant];

	return (
		<article className={theme.skeleton} aria-hidden>
			<div className="flex items-stretch gap-2">
				<div className="flex w-9 shrink-0 flex-col items-center justify-center gap-1.5 self-stretch">
					<div
						className={cn('h-7 w-6 rounded', theme.skeletonBlock)}
					/>
					<div
						className={cn('h-3 w-6 rounded', theme.skeletonBlock)}
					/>
				</div>
				<SessionCardColumnDivider variant={variant} />
				<div className="flex min-w-0 flex-1 flex-col gap-2 py-0.5">
					<div
						className={cn('h-4 w-24 rounded', theme.skeletonBlock)}
					/>
					<div
						className={cn(
							'h-3 w-full rounded',
							theme.skeletonBlock,
						)}
					/>
					<div
						className={cn('h-3 w-16 rounded', theme.skeletonBlock)}
					/>
				</div>
			</div>
		</article>
	);
};

const SESSION_LIST_SKELETON_COUNT = 4;

const ProntuarioSessionsListSkeleton = ({
	variant = 'dark',
}: {
	variant?: ProntuarioSessionCardVariant;
}) => (
	<ul
		className="space-y-2.5"
		aria-busy="true"
		aria-label="Carregando sessões"
	>
		{Array.from({ length: SESSION_LIST_SKELETON_COUNT }, (_, index) => (
			<li key={index}>
				<ProntuarioSessionCardSkeleton variant={variant} />
			</li>
		))}
	</ul>
);

const ProntuarioSessionCard = ({
	sessionNumber,
	dateLabel,
	shortSummary,
	shortSummaryTag,
	platformSession,
	onClick,
	variant = 'dark',
	isCurrent = false,
}: ProntuarioSessionCardProps) => {
	const theme = SESSION_CARD_THEME[variant];
	const hasShortSummary = Boolean(shortSummary?.trim());
	const summaryText =
		isCurrent && !hasShortSummary
			? 'Em andamento'
			: resolveShortSummaryDisplay(shortSummary);
	const schedule = resolveSessionCardSchedule(platformSession, dateLabel);

	return (
		<article
			onClick={onClick}
			className={cn(
				theme.card,
				isCurrent &&
					variant === 'dark' &&
					'relative border-[#8ab4f8]/30',
			)}
		>
			{isCurrent && variant === 'dark' ? (
				<span
					className="absolute inset-x-0 top-0 h-0.5 bg-[#8ab4f8]"
					aria-hidden
				/>
			) : null}
			<div
				className={cn(
					'flex items-stretch gap-2',
					isCurrent && variant === 'dark' && 'pt-0.5',
				)}
			>
				<div className="flex w-9 shrink-0 flex-col items-center justify-center gap-1 self-stretch">
					<span className={theme.day}>{schedule?.day ?? '—'}</span>
					<span className={theme.month}>
						{schedule?.monthShort ?? '—'}
					</span>
				</div>

				<SessionCardColumnDivider variant={variant} />

				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<p className={theme.sessionTitle}>
						Sessão {sessionNumber ?? '—'}
						{isCurrent ? (
							<span
								className={cn(
									variant === 'dark'
										? 'text-[#8ab4f8]'
										: 'text-blue-600',
								)}
							>
								{' '}
								(atual)
							</span>
						) : null}
					</p>
					<p
						className={cn(
							'truncate text-[12px] leading-snug',
							isCurrent && !hasShortSummary
								? variant === 'dark'
									? 'text-[#8ab4f8]'
									: 'text-blue-600'
								: hasShortSummary
								  ? theme.summaryWithText
								  : theme.summaryEmpty,
						)}
					>
						{summaryText}
					</p>
					<PbtsTagChips
						value={shortSummaryTag}
						variant={variant}
						className="mt-0.5"
					/>
				</div>

				<div className="ml-2 flex shrink-0 items-center self-center">
					<ChevronRight
						className={theme.chevron}
						strokeWidth={2.25}
						aria-hidden
					/>
				</div>
			</div>
		</article>
	);
};

type ProntuarioSessionsListProps = {
	sessions?: ProntuarioPastSessionRecord[];
	platformSessionByRecordId?: ReadonlyMap<string, Sessions.Session>;
	isLoading?: boolean;
	hasMorePastSessions?: boolean;
	isLoadingMorePastSessions?: boolean;
	onLoadMorePastSessions?: () => void;
	onOpenSession?: (sessionId: string) => void;
	variant?: ProntuarioSessionCardVariant;
};

const ProntuarioSessionsList = ({
	sessions = [],
	platformSessionByRecordId,
	isLoading = false,
	hasMorePastSessions = false,
	isLoadingMorePastSessions = false,
	onLoadMorePastSessions,
	onOpenSession,
	variant = 'dark',
}: ProntuarioSessionsListProps) => {
	const loadMoreButtonClass =
		variant === 'light'
			? 'mt-4 w-full rounded-lg border border-zinc-300 py-2.5 text-[14px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60'
			: 'mt-4 w-full rounded-lg border border-white/[0.08] py-2.5 text-[14px] font-medium text-[#8ab4f8] transition-colors hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-60';

	return (
		<div className="min-w-0">
			{isLoading ? (
				<ProntuarioSessionsListSkeleton variant={variant} />
			) : (
				<ul className="space-y-2.5">
					{sessions.map((record) => (
						<li key={record.id}>
							<ProntuarioSessionCard
								variant={variant}
								sessionNumber={record.sessaoNumero ?? '—'}
								dateLabel={record.data ?? '—'}
								shortSummary={record.resumoCurto}
								shortSummaryTag={
									record.resumoCurtoTag ?? record.resumoTag
								}
								platformSession={
									platformSessionByRecordId?.get(record.id) ??
									null
								}
								onClick={() => onOpenSession?.(record.id)}
							/>
						</li>
					))}
				</ul>
			)}

			{hasMorePastSessions ? (
				<button
					type="button"
					onClick={onLoadMorePastSessions}
					disabled={isLoadingMorePastSessions}
					className={loadMoreButtonClass}
				>
					{isLoadingMorePastSessions
						? 'Carregando...'
						: 'Mostrar mais'}
				</button>
			) : null}
		</div>
	);
};

export {
	ProntuarioSessionCard,
	ProntuarioSessionsList,
	ProntuarioSessionsListSkeleton,
	ProntuarioSessionCardSkeleton,
	resolveShortSummaryDisplay,
};
